"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Moon, Clock, TrendingUp, Calendar, BookOpen, Telescope, RefreshCcw } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function ProgressedPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-purple-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-purple-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-purple-500">/</span>
                <span className="text-sm">Progressed Haritalar</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Progressed Haritalar</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Moon className="h-10 w-10 text-purple-400" />
                Progressed Haritalar
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                "Bir gün bir yıl" prensibiyle gezegenlerin doğumdan sonraki hareketlerini izleyerek
                kişilik gelişimini ve yaşam olaylarını öngören en eski ve en temel progression tekniği.
                İç gelişimi ve rutin yaşam değişimlerini göstermede etkili bir araçtır.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavram</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <RefreshCcw className="h-5 w-5 text-purple-400" />
                      "Bir Gün Bir Yıl" Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Doğumdan sonraki her gün, yaşamın her yılına tekabül eder.
                        30. gün = 30. yıl, 50. gün = 50. yıl şeklinde hesaplanır.
                      </p>
                      <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-200 mb-2">Temel Formül:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Progressed Tarih = Doğum Tarihi + (Yaş) gün</div>
                          <div>25 yaş için: Doğum + 25 gün</div>
                          <div>Bu tarihteki efemeris pozisyonları kullanılır</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-indigo-400" />
                      Progressed Ay Döngüsü
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Progressed Ay yaklaşık 28-30 yılda zodiakı tamamlar.
                        En hızlı hareket eden progressed faktördür.
                      </p>
                      <div className="bg-indigo-900/20 border border-indigo-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-indigo-200 mb-2">Ayda Hareket:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Günlük: yaklaşık 12-15°</div>
                          <div>Aylık: yaklaşık 1 burç işareti</div>
                          <div>Yıllık: yaklaşık 12-13°</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Development */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Antik Dönem</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Progression konsepti Babil astrologlarına kadar uzanır.
                      Ptolemy'nin Tetrabiblos'unda sistematik olarak açıklanmıştır.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Babil astronomları:</strong> İlk progression kavramı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Ptolemy (MS 2. yy):</strong> Tetrabiblos'ta açıklama</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>İslam astronomları:</strong> Geliştirme ve yayılım</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Dönem</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      20. yüzyılda Alan Leo ve Marc Edmund Jones gibi astrologlar
                      tarafından psikolojik yorumlama dahil edildi.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Alan Leo:</strong> Modern psychological approach</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Marc Edmund Jones:</strong> Sabian Sembollerle entegrasyon</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Dane Rudhyar:</strong> Humanistic astrology yaklaşımı</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Progressed Factors */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ana Progressed Faktörler</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Progressed Sun */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☉</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Güneş</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Yıllık 1° İlerleme</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">30 yılda bir burç değiştirir</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">Yaşam amaçlarının evrimi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">Kişilik gelişimi ve odak değişimi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progressed Moon */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Ay</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">En Hızlı Progressed Faktör</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">2.5 yılda bir burç değiştirir</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Duygusal gelişim ve değişim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Günlük yaşam ve rutin değişiklikleri</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progressed Mercury */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☿</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Merkür</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Zihinsel Gelişim</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Güneş'e yakınlık nedeniyle yavaş hareket</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">İletişim tarzında değişiklikler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Öğrenme ve öğretme yaklaşımları</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progressed Venus */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♀</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Venüs</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İlişki ve Değer Evrimi</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">Aşk ve güzellik anlayışında değişim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">Finansal değerler ve kazanç yolları</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">Sanatsal zevk ve ifade gelişimi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progressed Mars */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Mars</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Eylem ve Motivasyon</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">2 yılda yaklaşık 1° ilerleme</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Mücadele tarzında değişim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Enerji kullanım biçimlerinde evrim</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progressed Angles */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">↗</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Progressed Açılar</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Yaşam Yönelimi</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">ASC: Kişilik ve görünüm değişimi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">MC: Kariyer ve itibar evrimi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">Yıllık 1° ilerleme</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Progressions */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Progression Dönemleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-cyan-400" />
                    Progressed New Moon
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Progressed Ay'ın Progressed Güneş'e conjunction yaptığı dönem.
                      Yeni başlangıçlar ve hayat döngüleri için kritik zamanlamalar.
                    </p>
                    <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2">Özellikler:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>• 29.5 yılda bir tekrarlanır</div>
                        <div>• Yeni yaşam fazının başlangıcı</div>
                        <div>• İç motivasyon ve dış amaçların uyumu</div>
                        <div>• Seed moment olayların tohumları atılır</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-400" />
                    Progressed Full Moon
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Progressed Ay'ın Progressed Güneş'e opposition yaptığı dönem.
                      Objective clarity ve yaşam dengesinde dönüm noktaları.
                    </p>
                    <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-200 mb-2">Manifestasyonlar:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>• New Moon'dan ~14-15 yıl sonra</div>
                        <div>• Maksimum aydınlanma ve farkındalık</div>
                        <div>• İç-dış dünya arasında gerilim</div>
                        <div>• Büyük kararlar ve yaklaşım değişimi</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulama</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-400" />
                      Analiz Teknikleri
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-200 mb-2">Adım Adım:</h4>
                        <div className="space-y-2 text-sm text-slate-300">
                          <div>1. Progressed pozisyonları hesapla</div>
                          <div>2. Progressed-Natal aspectleri incele</div>
                          <div>3. Progressed burç değişimlerini not et</div>
                          <div>4. Progressed Ay fazlarını belirle</div>
                          <div>5. House değişimlerini gözden geçir</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <RefreshCcw className="h-5 w-5 text-green-400" />
                      Yorumlama Anahtarları
                    </h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Progressed Ay en aktif ve gözlenebilir</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Burç değişimleri büyük gelişimleri işaret eder</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Aspectlerin orb'u dar tutulmalıdır (1°)</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Progressed/Transit kombinasyonları güçlüdür</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Life Cycle Examples */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Döngüsü Örnekleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-emerald-300">Progressed Ay Boğa'ya Geçiş</h3>
                  <div className="space-y-3">
                    <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-4">
                      <div className="font-medium text-emerald-200 mb-2">2.5 Yıllık Dönem Özellikleri:</div>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Maddi güvenlik arayışı</div>
                        <div>• Pratik ve somut hedefler</div>
                        <div>• Sabır ve sürdürülebilirlik</div>
                        <div>• Estetik ve sanat ilgisi artışı</div>
                        <div>• Finansal konularda odaklanma</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-blue-300">Progressed Güneş 7. House'a Geçiş</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">30 Yıllık Büyük Yaşam Fokusu:</div>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• İlişkilere odaklanma dönemi</div>
                        <div>• Ortaklıklar ve işbirlikleri</div>
                        <div>• Diğerleri üzerinden öğrenme</div>
                        <div>• Diplomasi ve uzlaşma becerileri</div>
                        <div>• Evlilik ve ciddi bağlantılar</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Klasik Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Progressed Horoscope" - Alan Leo</div>
                    <div>• "Progressions and Directions" - C.E.O. Carter</div>
                    <div>• "The Lunar Progressed Moon" - Sophia Mason</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Yaklaşımlar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Progressed Moon" - Bil Tierney</div>
                    <div>• "Astrology and the Evolution of Consciousness" - Maurice Fernandez</div>
                    <div>• "The Circle of Houses" - Bil Tierney</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Progressed haritalar kişilik gelişimi ve iç değişimleri gösteren kompleks bir sistemdir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel progressed chart analizi için deneyimli astrologlarla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/solar-arc"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Solar Arc Direktleri
              </Link>

              <Link
                href="/about/astrology/advanced-systems/fixed-stars"
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Sabit Yıldızlar
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}